import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Modal, ModalCloseButton } from '../../components/Modal.js'
import { Button, Field, Input, Select } from '../../components/ui.js'
import { errorMessage, fieldErrors, useTRPC } from '../../lib/trpc.js'

/**
 * Inviting somebody, behind the New person button.
 *
 * It used to be a card permanently at the top of the people screen, which cost a
 * third of the first fold to something done a handful of times a year. The list
 * is what the screen is for.
 */
export function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="invite-title" className="max-w-md">
      {open ? <Body onClose={onClose} /> : null}
    </Modal>
  )
}

function Body({ onClose }: { onClose: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const roles = useQuery(trpc.users.roles.queryOptions())
  const branches = useQuery(trpc.settings.branches.queryOptions())

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [branchId, setBranchId] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)

  const invite = useMutation(
    trpc.users.invite.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.users.pathFilter())
        setSentTo(email)
        setName('')
        setEmail('')
      },
    }),
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    invite.mutate({
      name,
      email,
      roleId: roleId || (roles.data?.[0]?.id ?? ''),
      ...(branchId ? { branchId } : {}),
    })
  }

  const errors = fieldErrors(invite.error)

  return (
    <>
      <div className="flex items-start gap-3 border-b border-ink-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 id="invite-title" className="text-sm font-semibold text-ink-900">
            Invite someone
          </h2>
          <p className="text-xs text-ink-500">
            They can be given work straight away — the account exists before they accept.
          </p>
        </div>
        <ModalCloseButton onClose={onClose} />
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3 p-4" noValidate>
        <Field label="Name" htmlFor="invite-name" error={errors['name']} required>
          <Input
            id="invite-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </Field>

        <Field label="Email" htmlFor="invite-email" error={errors['email']} required>
          <Input
            id="invite-email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </Field>

        <Field label="Role" htmlFor="invite-role">
          <Select
            id="invite-role"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
          >
            {(roles.data ?? []).map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>
        </Field>

        {(branches.data ?? []).length > 0 ? (
          <Field label="Branch" htmlFor="invite-branch">
            <Select
              id="invite-branch"
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
            >
              <option value="">No branch</option>
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {invite.error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage(invite.error)}
          </p>
        ) : null}
        {sentTo && !invite.error ? (
          <p className="text-sm text-emerald-700">Invitation sent to {sentTo}.</p>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
          <Button type="submit" variant="primary" loading={invite.isPending}>
            Send invitation
          </Button>
        </div>
      </form>
    </>
  )
}
